import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Copy, Phone, PlayCircle, RefreshCw, Send } from 'react-feather';

import {
  Badge,
  Button,
  Callout,
  Card,
  DescriptionList,
  Disclosure,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  Select,
  StatusBadge,
  Table,
  TextArea,
  TextInput,
} from '../../../ui';
import {
  CandidateDetail as Detail,
  FUNNEL,
  Genre,
  HiringMeta,
  Interview,
  PARKED,
  SOURCE_LABELS,
  STAGE_LABELS,
  STAGE_STATUS_LABELS,
  ScreeningCall,
  Stage,
  addNote,
  callNow,
  canMove,
  decideInterview,
  errorText,
  getCandidate,
  getGenres,
  INTERVIEW_LANGUAGE_LABELS,
  getInterviewRecording,
  getInterviewVideo,
  getMeta,
  nextStage,
  rescoreCall,
  rescoreInterview,
  resendInvite,
  scheduleAcademic,
  updateCandidate,
  when,
} from '../../../TeacherHiring/hiringApi';
import MoveDialog from './MoveDialog';
import './teacherHiring.scss';

const humanize = (value: string) => value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

const Transcript: React.FC<{ text: string | null }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <Disclosure summary="Transcript" open={open} onToggle={() => setOpen((o) => !o)}>
      <pre className="th-transcript">{text}</pre>
    </Disclosure>
  );
};

// --- Screening -------------------------------------------------------------

export const ScreeningPanel: React.FC<{ detail: Detail; onChange: () => void }> = ({ detail, onChange }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const calls = detail.screeningCalls;
  const latest = calls.find((c) => c.status === 'COMPLETED') ?? calls[0];

  const run = async (key: string, action: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await action();
      toast.success(done);
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const questionText = (call: ScreeningCall, id: string) => call.questionnaireSnapshot?.find((q) => q.id === id)?.text ?? humanize(id);

  return (
    <Card
      title="Screening call"
      subtitle="AI voice call with the genre's questionnaire, scored by AI. You decide."
      actions={
        detail.stage === 'SCREENING' && (
          <>
            <Button size="sm" variant="secondary" icon={<Phone size={14} />} loading={busy === 'call'}
              onClick={() => run('call', () => callNow(detail.id, true), 'Call queued; dialling within a minute')}>
              Call now
            </Button>
          </>
        )
      }
    >
      {calls.length === 0 ? (
        <EmptyState title="No calls yet" description={detail.stage === 'SCREENING' ? 'A call is placed in the next calling window.' : 'Calls start when the candidate enters Screening.'} />
      ) : (
        <div className="th-stack">
          {latest?.assessment && (
            <div className="th-assessment">
              <div className="th-assessment__head">
                <span className="th-assessment__score ds-num">{latest.assessment.overallScore}</span>
                <span className="ds-text-subtle">/ 100</span>
                <StatusBadge status={latest.assessment.recommendation} label={`AI recommends ${latest.assessment.recommendation.toLowerCase()}`} />
              </div>
              <p>{latest.assessment.summary}</p>
              {latest.assessment.knockout && <Callout tone="danger" title="Knockout">{latest.assessment.knockout}</Callout>}
              {latest.assessment.redFlags.length > 0 && (
                <Callout tone="warning" title="Red flags">
                  <ul className="th-list">{latest.assessment.redFlags.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </Callout>
              )}
              <Table>
                <thead><tr><th>Question</th><th>Answer</th><th className="ds-num">Score</th></tr></thead>
                <tbody>
                  {latest.assessment.answers.map((a) => (
                    <tr key={a.questionId}>
                      <td>{questionText(latest, a.questionId)}</td>
                      <td>{a.answer}{a.comment && <div className="ds-text-subtle">{a.comment}</div>}</td>
                      <td className="ds-num">{a.score}/5</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {latest && latest.status === 'COMPLETED' && !latest.assessment && (
            <Callout tone="info">Not scored by AI. Read the transcript and decide.</Callout>
          )}

          <Table>
            <thead><tr><th>Attempt</th><th>Status</th><th>When</th><th className="ds-num">Length</th><th /></tr></thead>
            <tbody>
              {calls.map((c) => (
                <tr key={c.id}>
                  <td className="ds-num">#{c.attempt}</td>
                  <td>
                    <StatusBadge status={c.status} label={humanize(c.status)} />
                    {(c.endedReason || c.error) && <div className="ds-text-subtle">{c.error ?? c.endedReason}</div>}
                  </td>
                  <td>{c.status === 'QUEUED' ? `Scheduled ${when(c.scheduledFor)}` : when(c.startedAt ?? c.createdAt)}</td>
                  <td className="ds-num">{c.durationSeconds ? `${Math.round(c.durationSeconds / 60)} min` : '—'}</td>
                  <td className="th-actions">
                    {c.status === 'COMPLETED' && c.transcript && (
                      <Button size="sm" variant="ghost" icon={<RefreshCw size={14} />} loading={busy === c.id}
                        onClick={() => run(c.id, () => rescoreCall(c.id), 'Re-scored')}>
                        Re-score
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {latest?.recordingUrl && <audio className="th-audio" controls preload="none" src={latest.recordingUrl} />}
          <Transcript text={latest?.transcript ?? null} />
        </div>
      )}
    </Card>
  );
};

// --- Interview 1 (video) -----------------------------------------------------

const Decision: React.FC<{ interview: Interview; onChange: () => void }> = ({ interview, onChange }) => {
  const [result, setResult] = useState(interview.result ?? '');
  const [feedback, setFeedback] = useState(interview.feedback ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await decideInterview(interview.id, { result: result || undefined, feedback: feedback.trim() || undefined });
      toast.success('Verdict saved');
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="th-form">
      <div className="th-form__row">
        <Field label="Verdict">
          {(ctl) => (
            <Select {...ctl} value={result} onChange={(e) => setResult(e.target.value)}
              options={[{ value: '', label: 'Not decided' }, { value: 'PASS', label: 'Pass' }, { value: 'HOLD', label: 'Hold' }, { value: 'FAIL', label: 'Fail' }]} />
          )}
        </Field>
        <Field label="Feedback" className="th-form__grow">
          {(ctl) => <TextInput {...ctl} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="What you saw in the video and interview" />}
        </Field>
      </div>
      <div><Button size="sm" variant="secondary" onClick={save} loading={saving}>Save verdict</Button></div>
      {interview.decidedBy && <p className="ds-text-subtle">Last decided by {interview.decidedBy}</p>}
    </div>
  );
};

export const AiInterviewPanel: React.FC<{ detail: Detail; onChange: () => void }> = ({ detail, onChange }) => {
  const interview = detail.interviews.find((i) => i.round === 'AI_AVATAR');
  const [busy, setBusy] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);

  const run = async (key: string, action: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await action();
      toast.success(done);
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const openVideo = async () => {
    if (!interview) return;
    setBusy('video');
    try {
      const { url } = await getInterviewVideo(interview.id);
      setVideoUrl(url);
      if (interview.performanceVideoUrl) window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const copyLink = () => {
    if (!detail.portalUrl) return;
    navigator.clipboard?.writeText(detail.portalUrl).then(() => toast.success('Interview link copied'));
  };

  const hasVideo = Boolean(interview?.performanceVideoKey || interview?.performanceVideoUrl || interview?.performanceRecordedAt);
  const videoSource = !interview
    ? ''
    : interview.performanceVideoUrl
      ? 'Shared as a link'
      : interview.performanceRecordedAt
        ? `Recorded on the portal, ${when(interview.performanceRecordedAt)}`
        : 'Uploaded file';
  const interviewDone = Boolean(interview?.transcript) || interview?.status === 'COMPLETED';
  const recordingRef = useRef<HTMLVideoElement | null>(null);
  const a = interview?.assessment;

  return (
    <Card
      title="Interview 1: Video interview"
      subtitle="The candidate answers the interview questions on camera from their portal, then records a performance video."
      actions={
        detail.stage === 'INTERVIEW_1' && (
          <>
            {detail.portalUrl && <Button size="sm" variant="ghost" icon={<Copy size={14} />} onClick={copyLink}>Copy link</Button>}
            <Button size="sm" variant="secondary" icon={<Send size={14} />} loading={busy === 'invite'}
              onClick={() => run('invite', () => resendInvite(detail.id), 'Invite sent')}>
              Resend invite
            </Button>
          </>
        )
      }
    >
      {!interview ? (
        <EmptyState title="Not invited yet" description="The invite goes out when the candidate moves to Interview 1." />
      ) : (
        <div className="th-stack">
          <DescriptionList
            items={[
              { label: 'Status', value: <StatusBadge status={interview.status} label={humanize(interview.status)} /> },
              { label: 'Consent', value: interview.consentAt ? `${interview.consentName}, ${when(interview.consentAt)} (v${interview.consentVersion})` : 'Not yet' },
            ]}
          />

          <section className="th-media">
            <div className="th-media__head">
              <h3 className="th-subhead">1. Video interview</h3>
              <span className="ds-text-subtle">
                {interviewDone ? 'Completed' : interview.status === 'IN_PROGRESS' ? 'In progress' : 'Not started'}
                {interview.language && interview.language !== 'en' && ` · questions in ${INTERVIEW_LANGUAGE_LABELS[interview.language]}`}
              </span>
            </div>
            {(interview.recordingUrl || interview.recordingPrefix) && !recordingUrl && (
              <div>
                <Button size="sm" variant="secondary" icon={<PlayCircle size={14} />} loading={busy === 'recording'}
                  onClick={async () => {
                    setBusy('recording');
                    try {
                      setRecordingUrl((await getInterviewRecording(interview.id)).url);
                    } catch (e) {
                      toast.error(errorText(e));
                    } finally {
                      setBusy(null);
                    }
                  }}>
                  Watch the interview
                </Button>
              </div>
            )}
            {recordingUrl && <video ref={recordingRef} className="th-video" controls src={recordingUrl} />}
            {interview.questionMarkers && interview.questionMarkers.length > 0 && (
              <ol className="th-markers">
                {interview.questionMarkers.map((m) => (
                  <li key={m.index}>
                    <button type="button" className="ds-link th-markers__jump" disabled={!recordingUrl}
                      title={recordingUrl ? 'Jump to this answer' : 'Open the recording first'}
                      onClick={() => {
                        const v = recordingRef.current;
                        if (!v) return;
                        v.currentTime = m.atSeconds;
                        void v.play();
                      }}>
                      {Math.floor(m.atSeconds / 60)}:{String(m.atSeconds % 60).padStart(2, '0')}
                    </button>
                    <span>{m.question}</span>
                  </li>
                ))}
              </ol>
            )}
            {interviewDone && interview.provider === 'LOCAL' && (
              <Callout tone="info">Recorded without Agora configured, so there is no video for this interview.</Callout>
            )}
          </section>

          <section className="th-media">
            <div className="th-media__head">
              <h3 className="th-subhead">2. Performance video</h3>
              <span className="ds-text-subtle">{hasVideo ? videoSource : 'Not submitted'}</span>
            </div>
            {hasVideo && !videoUrl && (
              <div>
                <Button size="sm" variant="secondary" icon={<PlayCircle size={14} />} loading={busy === 'video'} onClick={openVideo}>
                  {interview.performanceVideoUrl ? 'Open the link' : 'Watch the performance'}
                </Button>
              </div>
            )}
            {videoUrl && !interview.performanceVideoUrl && <video className="th-video" controls src={videoUrl} />}
          </section>

          {a && (
            <div className="th-assessment">
              <div className="th-assessment__head">
                <span className="th-assessment__score ds-num">{a.overallScore}</span>
                <span className="ds-text-subtle">/ 100</span>
                <StatusBadge status={a.recommendation} label={`AI recommends ${a.recommendation.toLowerCase()}`} />
                <Button size="sm" variant="ghost" icon={<RefreshCw size={14} />} loading={busy === 'rescore'}
                  onClick={() => run('rescore', () => rescoreInterview(interview.id), 'Re-scored')}>Re-score</Button>
              </div>
              <p>{a.summary}</p>
              <Table>
                <thead><tr><th>Dimension</th><th className="ds-num">Score</th><th>Evidence</th></tr></thead>
                <tbody>
                  {a.dimensions.map((d) => (
                    <tr key={d.key}><td>{humanize(d.key)}</td><td className="ds-num">{d.score}/5</td><td>{d.evidence}</td></tr>
                  ))}
                </tbody>
              </Table>
              <div className="th-two">
                <div><strong>Strengths</strong><ul className="th-list">{a.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                <div><strong>Concerns</strong><ul className="th-list">{a.concerns.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
              </div>
            </div>
          )}
          {interview.transcript && !a && hasVideo && <Callout tone="info">Not scored by AI. Read the transcript and watch the video.</Callout>}
          <Transcript text={interview.transcript} />
          {interviewDone && <Decision interview={interview} onChange={onChange} />}
        </div>
      )}
    </Card>
  );
};

// --- Interview 2 (Academic) --------------------------------------------------

const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export const AcademicPanel: React.FC<{ detail: Detail; meta: HiringMeta | null; onChange: () => void }> = ({ detail, meta, onChange }) => {
  const interview = detail.interviews.find((i) => i.round === 'ACADEMIC' && i.status !== 'CANCELLED');
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(interview?.scheduledAt ?? null));
  const [interviewerEmail, setInterviewerEmail] = useState(interview?.interviewerEmail ?? '');
  const [meetingUrl, setMeetingUrl] = useState(interview?.meetingUrl ?? '');
  const [scores, setScores] = useState<Record<string, number>>(interview?.scorecard?.scores ?? {});
  const [levelFit, setLevelFit] = useState(interview?.scorecard?.levelFit ?? '');
  const [notes, setNotes] = useState(interview?.scorecard?.notes ?? '');
  const [result, setResult] = useState(interview?.result ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const criteria = meta?.academicCriteria ?? [];

  const schedule = async () => {
    if (!scheduledAt || !interviewerEmail || !meetingUrl) {
      toast.error('Date, interviewer and meeting link are all needed');
      return;
    }
    setBusy('schedule');
    try {
      await scheduleAcademic(detail.id, { scheduledAt: new Date(scheduledAt).toISOString(), interviewerEmail: interviewerEmail.trim(), meetingUrl: meetingUrl.trim() });
      toast.success('Scheduled; the candidate has been told');
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const saveScorecard = async () => {
    if (!interview) return;
    if (criteria.some((c) => !scores[c.key])) {
      toast.error('Score every criterion');
      return;
    }
    setBusy('score');
    try {
      await decideInterview(interview.id, { result: result || undefined, scorecard: { scores, levelFit: levelFit || undefined, notes } });
      toast.success('Scorecard saved');
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  if (detail.stage !== 'INTERVIEW_2' && !interview) return null;

  return (
    <Card title="Interview 2: Academic interview" subtitle="A faculty member assesses performance, theory and a short teaching demo.">
      <div className="th-stack">
        {detail.stage === 'INTERVIEW_2' && (
          <div className="th-form">
            <div className="th-form__row">
              <Field label="Date and time">{(ctl) => <TextInput {...ctl} type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />}</Field>
              <Field label="Interviewer email">{(ctl) => <TextInput {...ctl} type="email" value={interviewerEmail} onChange={(e) => setInterviewerEmail(e.target.value)} />}</Field>
            </div>
            <Field label="Meeting link">{(ctl) => <TextInput {...ctl} type="url" placeholder="https://meet.google.com/…" value={meetingUrl} onChange={(e) => setMeetingUrl(e.target.value)} />}</Field>
            <div>
              <Button size="sm" variant="secondary" onClick={schedule} loading={busy === 'schedule'}>
                {interview ? 'Reschedule and notify' : 'Schedule and notify'}
              </Button>
            </div>
          </div>
        )}

        {interview && (
          <>
            <DescriptionList
              items={[
                { label: 'Status', value: <StatusBadge status={interview.status} label={humanize(interview.status)} /> },
                { label: 'When', value: when(interview.scheduledAt) },
                { label: 'Interviewer', value: interview.interviewerEmail },
                { label: 'Score', value: interview.score != null ? `${interview.score}/100` : '—' },
              ]}
            />
            <h3 className="th-subhead">Scorecard</h3>
            <div className="th-scorecard">
              {criteria.map((c) => (
                <Field key={c.key} label={c.label}>
                  {(ctl) => (
                    <Select {...ctl} size="sm" value={String(scores[c.key] ?? '')}
                      onChange={(e) => setScores((s) => ({ ...s, [c.key]: Number(e.target.value) }))}
                      options={[{ value: '', label: '–' }, ...[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))]} />
                  )}
                </Field>
              ))}
            </div>
            <div className="th-form__row">
              <Field label="Level they can teach">{(ctl) => <TextInput {...ctl} value={levelFit} onChange={(e) => setLevelFit(e.target.value)} placeholder="e.g. Beginner–Intermediate" />}</Field>
              <Field label="Verdict">
                {(ctl) => (
                  <Select {...ctl} value={result} onChange={(e) => setResult(e.target.value)}
                    options={[{ value: '', label: 'Not decided' }, { value: 'PASS', label: 'Pass' }, { value: 'HOLD', label: 'Hold' }, { value: 'FAIL', label: 'Fail' }]} />
                )}
              </Field>
            </div>
            <Field label="Notes">{(ctl) => <TextArea {...ctl} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />}</Field>
            <div><Button size="sm" variant="secondary" onClick={saveScorecard} loading={busy === 'score'}>Save scorecard</Button></div>
          </>
        )}
      </div>
    </Card>
  );
};

// --- Profile, messages, timeline --------------------------------------------

export const ProfileCard: React.FC<{ detail: Detail; genres: Genre[]; onChange: () => void }> = ({ detail, genres, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const genreName = new Map(genres.map((g) => [g.id, g.name]));

  const start = () => {
    setForm({
      email: detail.email ?? '',
      city: detail.city ?? '',
      genre: detail.genres[0] ?? '',
      experienceYears: detail.experienceYears?.toString() ?? '',
      ownerEmail: detail.ownerEmail ?? '',
      portfolioUrl: detail.portfolioUrl ?? '',
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateCandidate(detail.id, {
        email: form.email.trim() || undefined,
        city: form.city.trim() || undefined,
        genres: form.genre ? [form.genre, ...detail.genres.filter((g) => g !== form.genre)] : undefined,
        experienceYears: form.experienceYears ? Number(form.experienceYears) : undefined,
        ownerEmail: form.ownerEmail.trim(),
        portfolioUrl: form.portfolioUrl.trim() || undefined,
      });
      toast.success('Saved');
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Card
      title="Profile"
      actions={
        editing ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button size="sm" variant="secondary" onClick={save} loading={saving}>Save</Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" onClick={start}>Edit</Button>
        )
      }
    >
      {editing ? (
        <div className="th-form">
          <Field label="Owner (hiring agent email)">{(ctl) => <TextInput {...ctl} type="email" value={form.ownerEmail} onChange={set('ownerEmail')} />}</Field>
          <Field label="Email">{(ctl) => <TextInput {...ctl} type="email" value={form.email} onChange={set('email')} />}</Field>
          <Field label="City">{(ctl) => <TextInput {...ctl} value={form.city} onChange={set('city')} />}</Field>
          <Field label="Primary genre" hint="Decides the screening questionnaire and brochure.">
            {(ctl) => (
              <Select {...ctl} value={form.genre} onChange={set('genre')}
                options={[{ value: '', label: 'Not known' }, ...genres.map((g) => ({ value: g.id, label: g.name }))]} />
            )}
          </Field>
          <Field label="Years teaching">{(ctl) => <TextInput {...ctl} numeric type="number" min={0} max={70} value={form.experienceYears} onChange={set('experienceYears')} />}</Field>
          <Field label="Portfolio link">{(ctl) => <TextInput {...ctl} type="url" value={form.portfolioUrl} onChange={set('portfolioUrl')} />}</Field>
        </div>
      ) : (
        <DescriptionList
          items={[
            { label: 'Phone', value: <a className="ds-link" href={`tel:${detail.phone}`}>{detail.phone}</a> },
            { label: 'Email', value: detail.email },
            { label: 'City', value: detail.city },
            { label: 'Genres', value: detail.genres.map((g) => genreName.get(g) ?? g).join(', ') || null },
            { label: 'Years teaching', value: detail.experienceYears },
            { label: 'Languages', value: detail.languages.join(', ') || null },
            { label: 'Qualification', value: detail.qualification },
            { label: 'Portfolio', value: detail.portfolioUrl && <a className="ds-link" href={detail.portfolioUrl} target="_blank" rel="noreferrer">Open</a> },
            { label: 'Source', value: `${SOURCE_LABELS[detail.source]}${detail.sourceDetail ? ` · ${detail.sourceDetail}` : ''}` },
            { label: 'Referred by', value: detail.referrerTeacherName },
            { label: 'Owner', value: detail.ownerEmail },
            { label: 'Applied', value: when(detail.createdAt) },
            ...(detail.trainingBatch ? [{ label: 'Training batch', value: `${detail.trainingBatch}${detail.trainingStartAt ? `, ${when(detail.trainingStartAt)}` : ''}` }] : []),
            ...(detail.contractUrl ? [{ label: 'Contract', value: <a className="ds-link" href={detail.contractUrl} target="_blank" rel="noreferrer">{detail.contractSignedAt ? `Signed ${when(detail.contractSignedAt)}` : 'Sent'}</a> }] : []),
            ...(detail.cmsTeacherId ? [{ label: 'CMS teacher id', value: detail.cmsTeacherId }] : []),
          ]}
        />
      )}
    </Card>
  );
};

export const Timeline: React.FC<{ detail: Detail; onChange: () => void }> = ({ detail, onChange }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await addNote(detail.id, note.trim());
      setNote('');
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card title="Timeline">
      <div className="th-form">
        <Field label="Add a note">{(ctl) => <TextArea {...ctl} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
        <div><Button size="sm" variant="secondary" onClick={save} loading={saving} disabled={!note.trim()}>Add note</Button></div>
      </div>
      <ol className="th-timeline">
        {detail.events.map((e) => (
          <li key={e.id}>
            <div className="th-timeline__what">
              {e.type === 'STAGE_CHANGED' && e.toStage
                ? `Moved${e.fromStage ? ` from ${STAGE_LABELS[e.fromStage]}` : ''} to ${STAGE_LABELS[e.toStage]}`
                : humanize(e.type)}
            </div>
            {e.note && <div className="th-timeline__note">{e.note}</div>}
            <div className="ds-text-subtle">{e.actor} · {when(e.createdAt)}</div>
          </li>
        ))}
      </ol>
    </Card>
  );
};

export const Messages: React.FC<{ detail: Detail }> = ({ detail }) =>
  detail.messages.length === 0 ? null : (
    <Card title="Messages sent" flush>
      <Table>
        <tbody>
          {detail.messages.map((m) => (
            <tr key={m.id}>
              <td>{humanize(m.kind)}<div className="ds-text-subtle">{m.channel === 'WHATSAPP' ? 'WhatsApp' : 'Email'} · {when(m.createdAt)}</div></td>
              <td><StatusBadge status={m.status} label={humanize(m.status)} />{m.error && <div className="ds-text-subtle">{m.error}</div>}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );

// --- Page --------------------------------------------------------------------

const CandidateDetail: React.FC = () => {
  const { id = '' } = useParams();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [meta, setMeta] = useState<HiringMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState<Stage | null>(null);
  const [pickStage, setPickStage] = useState('');

  const load = useCallback(async () => {
    try {
      setDetail(await getCandidate(id));
      setError(null);
    } catch (e) {
      setError(errorText(e));
    }
  }, [id]);

  useEffect(() => {
    load();
    getGenres().then(setGenres).catch(() => undefined);
    getMeta().then(setMeta).catch(() => undefined);
  }, [load]);

  // While something is happening on the candidate's side (a call, the portal), refresh quietly.
  useEffect(() => {
    if (detail?.stageStatus !== 'SCHEDULED' && detail?.stageStatus !== 'IN_PROGRESS') return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [detail?.stageStatus, load]);

  const moveOptions = useMemo(
    () => (detail ? [...FUNNEL, ...PARKED].filter((s) => canMove(detail.stage, s)) : []),
    [detail],
  );

  const back = (
    <Link to={`${process.env.PUBLIC_URL}/pages/teacher-hiring`} className="ds-link th-back">
      <ArrowLeft size={14} /> Teacher Recruitment
    </Link>
  );

  if (error && !detail) {
    return (
      <Page>
        {back}
        <Callout tone="danger" title="Couldn't load this candidate">{error}</Callout>
      </Page>
    );
  }
  if (!detail) return <Page><Loader label="Loading candidate…" /></Page>;

  const next = nextStage(detail.stage);
  const showScreening = detail.screeningCalls.length > 0 || detail.stage === 'SCREENING';
  const showAi = detail.interviews.some((i) => i.round === 'AI_AVATAR') || detail.stage === 'INTERVIEW_1';

  return (
    <Page className="th-page">
      {back}
      <PageHeader
        eyebrow="Academics · Teacher Recruitment"
        title={detail.name}
        subtitle={
          <span className="th-subtitle">
            <Badge tone="primary">{STAGE_LABELS[detail.stage]}</Badge>
            <StatusBadge status={detail.stageStatus} label={STAGE_STATUS_LABELS[detail.stageStatus]} />
            {detail.rejectionReason && <span className="ds-text-subtle">{detail.rejectionReason}</span>}
          </span>
        }
        actions={
          <>
            <Select
              aria-label="Move to another stage"
              value={pickStage}
              onChange={(e) => {
                setPickStage('');
                if (e.target.value) setMoveTo(e.target.value as Stage);
              }}
              options={[{ value: '', label: 'Move to…' }, ...moveOptions.map((s) => ({ value: s, label: STAGE_LABELS[s] }))]}
            />
            {next && canMove(detail.stage, next) && <Button onClick={() => setMoveTo(next)}>Move to {STAGE_LABELS[next]}</Button>}
            {detail.stage === 'ON_HOLD' && <Button onClick={() => setMoveTo('SCREENING')}>Resume</Button>}
          </>
        }
      />

      <div className="th-detail">
        <div className="th-detail__main">
          {detail.stage === 'NEW' && (
            <Callout tone="info" title="Not screened yet">Move them to Screening to start the AI call.</Callout>
          )}
          {(detail.stage === 'INTERVIEW_2' || detail.interviews.some((i) => i.round === 'ACADEMIC')) && (
            <AcademicPanel key={detail.interviews.find((i) => i.round === 'ACADEMIC')?.id ?? 'new'} detail={detail} meta={meta} onChange={load} />
          )}
          {showAi && <AiInterviewPanel detail={detail} onChange={load} />}
          {showScreening && <ScreeningPanel detail={detail} onChange={load} />}
          <Timeline detail={detail} onChange={load} />
        </div>
        <aside className="th-detail__side">
          <ProfileCard detail={detail} genres={genres} onChange={load} />
          <Messages detail={detail} />
        </aside>
      </div>

      <MoveDialog
        candidate={moveTo ? { id: detail.id, name: detail.name, stage: detail.stage } : null}
        toStage={moveTo}
        onClose={() => setMoveTo(null)}
        onMoved={load}
      />
    </Page>
  );
};

export default CandidateDetail;
